// Port of SuiteModel/SettingsJSON.swift (`decodeJSON`/`putJSON`, `JSONValue.graft`, `JSONCoding`, `lossyArray`): settings
// kept as JSON strings (revisions, beat sheet, collections, glossary entries…) are read by builds old and new. Reading is
// tolerant: arrays decode element by element, and what can't be read at all reads as the default. Writing never loses
// what this build couldn't read — keys it doesn't know and array elements it couldn't decode are grafted back into the
// new value — and a stored value that doesn't decode at all is left alone: the setter throws `ModelError.invalid`
// (`unreadable`) rather than write over it.
//
// Written as `JSONEncoder` with `.sortedKeys` (and `.iso8601` dates) writes it: keys in Swift's string order, nil
// optionals left out, "/" escaped as "\/" (`suiteJSON` for typed values, `encodeSwiftJSON` for a grafted tree, whose
// nulls are values the stored JSON held and so are kept).
import { ModelError, suiteJSON } from './edit/storeEditor.ts';
import { str, type Path, type Writer } from './edit/typed.ts';
import { swiftCompare } from './model/order.ts';

export type JSONValue = null | boolean | number | string | JSONValue[] | { [key: string]: JSONValue };

/** A decoder in the Swift's sense: the value as this build reads it, or null (or a throw) when it can't be read at all. */
export type JSONDecode<T> = (json: unknown) => T | null;

const isObject = (v: unknown): v is Record<string, JSONValue> => !!v && typeof v === 'object' && !Array.isArray(v);
const idOf = (v: JSONValue): string | null => (isObject(v) && typeof v.id === 'string' ? v.id : null);

/** `JSONValue ==`. */
export function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => jsonEqual(x, b[i]));
  if (isObject(a) && isObject(b)) {
    const ka = Object.keys(a), kb = Object.keys(b);
    return ka.length === kb.length && ka.every((k) => k in b && jsonEqual(a[k], b[k]));
  }
  return false;
}

/** `JSONValue.graft(stored:roundTrip:into:)`: `next` with what `stored` held that this build dropped when it read it back
 *  (`roundTrip` = `stored` decoded and encoded again): keys it doesn't know, and array elements it couldn't decode
 *  (matched by `id`; for lists without ids, when `next` only appended to what was read). Keys this build knows follow
 *  `next`, so clearing an optional or removing an element still works. */
export function graft(stored: JSONValue, roundTrip: JSONValue, next: JSONValue): JSONValue {
  if (isObject(stored) && isObject(roundTrip) && isObject(next)) {
    const result: Record<string, JSONValue> = { ...next };
    for (const [key, value] of Object.entries(stored)) {
      if (key in roundTrip) {
        if (key in next) result[key] = graft(value, roundTrip[key], next[key]);
      } else if (!(key in next)) {
        result[key] = value;
      }
    }
    return result;
  }
  if (Array.isArray(stored) && Array.isArray(roundTrip) && Array.isArray(next)) {
    const sIDs = stored.map(idOf), rIDs = roundTrip.map(idOf), nIDs = next.map(idOf);
    if (!sIDs.includes(null) && !nIDs.includes(null) && !rIDs.includes(null)) {
      // `Dictionary(uniquingKeysWith: { a, _ in a })`: the first of a repeated id.
      const read = new Map<string, JSONValue>(), kept = new Map<string, JSONValue>();
      roundTrip.forEach((r, i) => { if (!read.has(rIDs[i]!)) read.set(rIDs[i]!, r); });
      stored.forEach((s, i) => { if (!kept.has(sIDs[i]!)) kept.set(sIDs[i]!, s); });
      const result = next.map((element) => {
        const id = idOf(element)!;
        const st = kept.get(id), rt = read.get(id);
        return st !== undefined && rt !== undefined ? graft(st, rt, element) : element;
      });
      // Elements this build couldn't read go back after the element they followed.
      stored.forEach((element, i) => {
        if (read.has(sIDs[i]!)) return;
        const before = sIDs.slice(0, i).reverse().find((id) => result.some((r) => idOf(r) === id));
        const at = before !== undefined ? result.findIndex((r) => idOf(r) === before) + 1 : 0;
        result.splice(at, 0, element);
      });
      return result;
    }
    if (stored.length === roundTrip.length && roundTrip.length === next.length) return next.map((n, i) => graft(stored[i], roundTrip[i], n));
    if (stored.length !== roundTrip.length && next.length >= roundTrip.length && jsonEqual(next.slice(0, roundTrip.length), roundTrip)) {
      return [...stored, ...next.slice(roundTrip.length)];
    }
    return next;
  }
  return next;
}

/** A typed value as `JSONEncoder` sees it, as a plain tree: dates `.iso8601` (whole seconds), nil optionals (undefined
 *  or null) left out of objects — what `suiteJSON` writes, before its key sorting. */
export const toJSONValue = (value: unknown): JSONValue => JSON.parse(suiteJSON(value)) as JSONValue;

/** `JSONCoding.encoder` over a tree: sorted keys (Swift's order), "/" escaped; nulls in the tree are written. */
export function encodeSwiftJSON(value: JSONValue): string {
  const sorted = (x: JSONValue): JSONValue => {
    if (Array.isArray(x)) return x.map(sorted);
    if (isObject(x)) {
      const out: Record<string, JSONValue> = {};
      for (const k of Object.keys(x).sort(swiftCompare)) out[k] = sorted(x[k]);
      return out;
    }
    return x;
  };
  return JSON.stringify(sorted(value)).replaceAll('/', '\\/');
}

/** `JSONCoding.unreadable`. */
export const unreadable = (what: string) => new ModelError('invalid',
  `This project’s ${what} can’t be read on this device — a newer version of Five Rooms may have saved them — so they weren’t changed. Update Five Rooms and try again.`);

/** `decodeJSON`: a settings value stored as JSON; null when absent, empty or unreadable (`decode` returning null or
 *  throwing). */
export function decodeJSON<T>(stored: unknown, decode: JSONDecode<T>): T | null {
  if (typeof stored !== 'string' || stored === '') return null;
  try {
    return decode(JSON.parse(stored)) ?? null;
  } catch {
    return null;
  }
}

/** `lossyArray`: the elements that decode (an absent or non-array value is empty); one that throws or reads as null is
 *  dropped, as `Lossy` drops it. */
export function lossyArray<T>(raw: unknown, decode: JSONDecode<T>): T[] {
  if (!Array.isArray(raw)) return [];
  const out: T[] = [];
  for (const element of raw) {
    try {
      const v = decode(element);
      if (v !== null && v !== undefined) out.push(v);
    } catch { /* dropped */ }
  }
  return out;
}

/**
 * `putJSON`'s string: `value` encoded (null stores ""), with what the stored string held that `decode` drops grafted
 * back. `decode` is this build's `Decodable` (null or a throw when it can't read the value); `encode` is its `Encodable`
 * as a plain object (default: the value itself), written with `suiteJSON`'s rules. Throws `unreadable(what)`, changing
 * nothing, when the stored value can't be read at all — also when clearing it.
 */
export function mergedJSON<T>(stored: unknown, value: T | null, decode: JSONDecode<T>, what: string,
  encode: (value: T) => unknown = (v) => v): string {
  const current = typeof stored === 'string' ? stored : '';
  const readable = (): { tree: JSONValue; read: T } => {
    try {
      const tree = JSON.parse(current) as JSONValue;
      const read = decode(tree);
      if (read !== null && read !== undefined) return { tree, read };
    } catch { /* unreadable */ }
    throw unreadable(what);
  };
  if (value === null) {
    if (current !== '') readable();
    return '';
  }
  const next = toJSONValue(encode(value));
  if (current === '') return encodeSwiftJSON(next);
  const { tree, read } = readable();
  const merged = graft(tree, toJSONValue(encode(read)), next);
  return encodeSwiftJSON(jsonEqual(merged, next) ? next : merged);
}

/** `Document.putJSON(_:_:_:what:)`: stores `mergedJSON` at `path`/`key` (only when it changes). */
export function putJSON<T>(w: Writer, path: Path, key: string, value: T | null, decode: JSONDecode<T>, what: string,
  encode?: (value: T) => unknown): void {
  w.putIfChanged(path, key, str(mergedJSON(storedString(w, path, key), value, decode, what, encode)));
}

/** The string stored at `path`/`key` ("" when there is none), in whichever of the maps at the path holds it. */
function storedString(w: Writer, path: Path, key: string): string {
  return w.string(path, key) ?? '';
}
